import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";

import {
    formatDecimal,
    formatKwh,
    formatKw,
    formatSek,
    formatSpotPricePerKwh,
    formatYearMonthLabel,
} from "./gridCostUtils";
import {
    GRID_PEAK_POWER_TOP3_AVERAGE,
} from "./gridCostConstants";
import Tooltip from "../Tooltip";

function SectionHeading({ children, className = "" }) {
    return (
        <h4
            className={`text-sm font-semibold text-slate-900 border-b border-slate-200 pb-1.5 mb-0 ${className}`.trim()}
        >
            {children}
        </h4>
    );
}

function marginToneClass(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount === 0) {
        return "text-slate-800";
    }

    return amount > 0 ? "text-emerald-700" : "text-red-700";
}

function marginSurfaceClass(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount === 0) {
        return "bg-slate-50 border-slate-200";
    }

    return amount > 0 ? "bg-emerald-50/90 border-emerald-200" : "bg-red-50/90 border-red-200";
}

function billingPeakHint(month, t) {
    if (month.peakPowerMode === GRID_PEAK_POWER_TOP3_AVERAGE) {
        return t("gridCost.peakHintTop3");
    }

    return t("gridCost.peakHintHighest");
}

function buildMonthRows(month, t) {
    return [
        {
            left: { label: t("gridCost.gridConnection"), value: formatKw(month.connectionKw) },
            right: { label: t("gridCost.capacityAndSubscription"), value: formatSek(month.k_effekt) },
        },
        {
            left: {
                label: t("gridCost.billingPeakPower"),
                hint: billingPeakHint(month, t),
                value: formatKw(month.p_peak),
            },
            right: { label: t("gridCost.energyTransferAndSpot"), value: formatSek(month.k_overforing) },
        },
        {
            left: {
                label: t("gridCost.energyDelivered"),
                hint: t("gridCost.energyDeliveredHint"),
                value: formatKwh(month.monthlyEnergyKWh),
            },
            right: {
                label: t("gridCost.spotElectricity"),
                hint: t("gridCost.spotHint"),
                value: formatSek(month.k_spot),
            },
        },
        {
            left: {
                label: t("gridCost.averageSpotPrice"),
                value: formatSpotPricePerKwh(month.monthlyAverageSpotSekPerKwh),
            },
            right: { label: t("gridCost.fixedMonthlyFee"), value: formatSek(month.k_fast) },
        },
        {
            left: {
                label: t("gridCost.chargingRevenueExclVat"),
                value: formatSek(month.monthlyRevenueExclVat),
            },
            right: { label: t("gridCost.energyTax"), value: formatSek(month.k_skatt) },
        },
        {
            left: { label: "", value: "" },
            right: {
                label: t("gridCost.totalCostExclVat"),
                value: formatSek(month.k_tot),
                highlight: true,
            },
        },
    ];
}

function getMarginMetrics(month) {
    const margin = month.monthlyMarginExclVat;
    const marginPercent = month.monthlyMarginPercent;

    return { margin, marginPercent };
}

function MarginValue({ margin, marginPercent }) {
    return (
        <span className={`font-semibold tabular-nums ${marginToneClass(margin)}`}>
            {formatSek(margin)}
            <span className="text-slate-900 font-normal mx-1.5"> / </span>
            {marginPercent == null ? "—" : `${formatDecimal(marginPercent, 1)}%`}
        </span>
    );
}

function MarginCompact({ month }) {
    const { t } = useTranslation();
    const { margin, marginPercent } = getMarginMetrics(month);

    return (
        <div className="flex justify-end">
            <div
                className={`inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-md border px-2.5 py-1.5 text-sm text-slate-900 ${marginSurfaceClass(margin)}`}
            >
                <span>{t("gridCost.marginExclVat")}</span>
                <MarginValue margin={margin} marginPercent={marginPercent} />
            </div>
        </div>
    );
}

function MobileSection({ title, rows, side }) {
    return (
        <div>
            <SectionHeading className="mb-2">{title}</SectionHeading>
            <table className="w-full">
                <tbody>
                    {rows.map((row) => {
                        const item = row[side];
                        if (!item.label) {
                            return null;
                        }
                        return (
                            <tr key={`${side}-${item.label}`} className={item.highlight ? "bg-primary-50/80 font-semibold" : ""}>
                                <td className="py-1.5 pr-3 text-sm text-slate-900 align-top">
                                    <span className="inline-flex items-center gap-1.5">
                                        <span>{item.label}</span>
                                        {item.hint ? (
                                            <Tooltip content={item.hint} position="right" tooltipClassName="max-w-sm">
                                                <span
                                                    className="inline-flex shrink-0 cursor-help text-slate-400 hover:text-slate-600"
                                                    aria-label={item.hint}
                                                >
                                                    <FontAwesomeIcon icon={faInfoCircle} className="h-3.5 w-3.5" />
                                                </span>
                                            </Tooltip>
                                        ) : null}
                                    </span>
                                </td>
                                <td className="py-1.5 text-sm text-right tabular-nums text-slate-900 align-top">{item.value}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function RowLabel({ label, hint }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <span>{label}</span>
            {hint ? (
                <Tooltip content={hint} position="right" tooltipClassName="max-w-sm">
                    <span
                        className="inline-flex shrink-0 cursor-help text-slate-400 hover:text-slate-600"
                        aria-label={hint}
                    >
                        <FontAwesomeIcon icon={faInfoCircle} className="h-3.5 w-3.5" />
                    </span>
                </Tooltip>
            ) : null}
        </span>
    );
}

function MonthCard({ month }) {
    const { t } = useTranslation();
    const tariffLabel =
        month.resolvedGridAreaTariffName ||
        month.resolvedGridAreaTariffPublicId ||
        "—";
    const rows = buildMonthRows(month, t);

    return (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="font-medium text-slate-900">{formatYearMonthLabel(month.yearMonth)}</span>
                <span className="text-slate-900">
                    {t("gridCost.tariff")} <span className="font-medium">{tariffLabel}</span>
                </span>
            </div>

            <div className="p-3 space-y-4 lg:hidden">
                <MobileSection title={t("gridCost.monthlyBasis")} rows={rows} side="left" />
                <MobileSection title={t("gridCost.electricityNetworkCosts")} rows={rows} side="right" />
            </div>

            <div className="hidden lg:block px-3 pt-3">
                <table className="w-full table-fixed">
                    <colgroup>
                        <col className="w-[22%]" />
                        <col className="w-[28%]" />
                        <col className="w-[22%]" />
                        <col className="w-[28%]" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th colSpan={2} className="pb-2 text-left">
                                <SectionHeading>{t("gridCost.monthlyBasis")}</SectionHeading>
                            </th>
                            <th colSpan={2} className="pb-2 pl-8 text-left">
                                <SectionHeading>{t("gridCost.electricityNetworkCosts")}</SectionHeading>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const highlightClass = row.right.highlight ? "bg-primary-50/80 font-semibold" : "";
                            return (
                            <tr key={`${row.left.label}-${row.right.label}`}>
                                <td className="py-1.5 pr-3 text-sm text-slate-900 align-top">
                                    {row.left.label ? <RowLabel label={row.left.label} hint={row.left.hint} /> : null}
                                </td>
                                <td className="py-1.5 pr-8 text-sm text-right tabular-nums text-slate-900 align-top">
                                    {row.left.value}
                                </td>
                                <td className={`py-1.5 pl-8 pr-3 text-sm text-slate-900 align-top ${highlightClass}`}>
                                    <RowLabel label={row.right.label} hint={row.right.hint} />
                                </td>
                                <td className={`py-1.5 text-sm text-right tabular-nums text-slate-900 align-top ${highlightClass}`}>{row.right.value}</td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="px-3 pb-3 pt-2">
                <MarginCompact month={month} />
            </div>
        </div>
    );
}

function SiteResultSection({ site }) {
    const { t } = useTranslation();
    const displaySiteName = site.siteName || site.sitePublicId || "—";

    if (site.error) {
        return (
            <section className="rounded-2xl border border-red-200 bg-red-50 p-4 shadow-lg">
                <div className="pb-2 mb-2 border-b border-red-200">
                    <h3 className="text-base font-semibold text-red-900">{displaySiteName}</h3>
                    {site.sitePublicId ? (
                        <p className="text-xs text-red-800 mt-0.5">{t("gridCost.siteId", { id: site.sitePublicId })}</p>
                    ) : null}
                </div>
                <p className="text-sm text-red-800">{site.error}</p>
            </section>
        );
    }

    const months = site.months || [];
    if (months.length === 0) {
        return (
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-lg">
                <div className="pb-2 mb-2 border-b border-slate-200">
                    <h3 className="text-base font-semibold">{displaySiteName}</h3>
                </div>
                <p className="text-sm text-mute-500">{t("gridCost.noMonthlyResults")}</p>
            </section>
        );
    }

    return (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-lg">
            <div className="pb-3 mb-3 border-b border-slate-200">
                <h3 className="text-base font-semibold">{displaySiteName}</h3>
                {site.sitePublicId ? (
                    <p className="text-xs text-slate-900 mt-0.5">{t("gridCost.siteId", { id: site.sitePublicId })}</p>
                ) : null}
            </div>

            <div className="space-y-3">
                {months.map((m) => (
                    <MonthCard key={`${site.sitePublicId}-${m.yearMonth}`} month={m} />
                ))}
            </div>
        </section>
    );
}

export default function GridCostResultView({ result }) {
    const { t } = useTranslation();
    if (!result) {
        return null;
    }

    const sites = result.sites || [];
    if (sites.length === 0) {
        return <p className="text-sm text-mute-500 mt-4">{t("gridCost.noSiteResults")}</p>;
    }

    return (
        <div className="space-y-4 mt-4">
            {sites.map((site) => (
                <SiteResultSection key={site.sitePublicId} site={site} />
            ))}
        </div>
    );
}
